import React, { useState, useEffect, useMemo } from "react";
import { graphql } from "gatsby";
import Layout from "../components/layout";
import Seo from "../components/seo";
import PLPIntro from "../components/plp-intro";
import ProductListCross from "../components/product-list-cross";
import SliderHero from "../components/slider-hero";
import withPreview from "../utility/with-preview";
import EditorialIntro from "../components/editorial-intro";
import { normalizeRelativePath } from "../utility/paths";
import CompareBar from "../components/compare-bar";
import { CompareProvider } from "../context/compare";
import { getCampaignBoxes } from "../utility/campaigns";
import { eventImpression } from "../utility/datalayer";
import placeholderLogoImg from "../images/logo_haier_search.jpg";
import Intro from "../components/intro";
import findHrefLang from "../utility/find-hreflang";
import ProductNotes from "../components/elements/product-notes";
import PageLineContentBody from "../components/page-line-content-body";
import slugify from "@sindresorhus/slugify";
import useTabs from "../hooks/use-tabs";
import CategoryModule from "../components/category-module";

const PreviewSlider = withPreview(SliderHero);

const PageCrossLineTemplate = ({
  data: {
    liferayPageCrossLine,
    config,
    labelManagerLabels,
    globalCampaignsBannerTop,
    liferayProductNotes,
    newMainMenuFiltered,
    liferayMenuConfiguration,
  },
  location,
}) => {
  const { pclLines, plcSkusProducts, pclIntro, campaignsPLP, campaignsBannerTop, categoryModule } =
    liferayPageCrossLine;
  const hasHero = useMemo(() => !!pclIntro, [pclIntro]);
  const products = useMemo(() => {
    const skuProducts = plcSkusProducts ? plcSkusProducts.filter(Boolean) : [];
    if (skuProducts.length) {
      return skuProducts
        .reduce((res, item) => {
          if (!res.some((el) => el.key_prod === item.key_prod)) {
            res.push(item);
          }
          return res;
        }, [])
        .map((item, i) => ({
          ...item,
          productPriority: skuProducts.length - i,
        }));
    }
    const lineProducts = pclLines?.map((item) => item.allBabelProdotto)?.flat();
    return lineProducts;
  }, [pclLines, plcSkusProducts]);

  const [impression, setImpression] = useState(false);
  useEffect(() => {
    if (!impression) {
      const eventCategory = liferayPageCrossLine.liferayFields?.taxonomyCategoryBriefs?.find(
        (category) => category?.parentTaxonomyVocabulary?.name === "events"
      );
      if (eventCategory) {
        eventImpression(liferayPageCrossLine.slug, eventCategory.name);
        setImpression(true);
      }
    }
  }, [liferayPageCrossLine, impression]);

  const isNewMenu =
    !!newMainMenuFiltered &&
    newMainMenuFiltered?.name &&
    liferayMenuConfiguration?.newMenuActive &&
    liferayMenuConfiguration?.copyrightText !== "";

  const breadcrumb = useMemo(
    () =>
      [
        {
          name: labelManagerLabels.home,
          link: "/",
        },
      ].concat(
        liferayPageCrossLine.parentPages?.length > 0
          ? liferayPageCrossLine.parentPages.map((item) => ({
              name:
                isNewMenu && item.menuItem?.name && item.menuItem?.useCustomName
                  ? item.menuItem.name
                  : item.page.liferayFields.title,
              link: item.page?.slug
                ? normalizeRelativePath(item.page.slug)
                : item.page?.externalLink || "",
            }))
          : []
      ),
    [liferayPageCrossLine, labelManagerLabels, isNewMenu]
  );

  const prodWithPrice = products.find((product) => product.productSellingPrice !== null);
  const priceData = useMemo(() => {
    if (prodWithPrice) {
      return {
        msrpPrice: {
          currency: config.currency,
          value: prodWithPrice.productMsrpPrice,
        },
        sellingPrice: {
          currency: config.currency,
          value: prodWithPrice.productSellingPrice,
        },
        omnibusPrice: prodWithPrice.productOmnibusPrice
          ? {
              currency: config.currency,
              value: prodWithPrice.productOmnibusPrice,
            }
          : null,
      };
    }
    return null;
  }, [prodWithPrice, config]);

  const campaignBoxesPromo = useMemo(
    () => getCampaignBoxes(campaignsPLP, priceData),
    [campaignsPLP, priceData]
  );

  const resultBannerPromoCm = campaignBoxesPromo.length ? campaignBoxesPromo : [];

  const campaignBoxesBannerTop = useMemo(
    () => getCampaignBoxes(campaignsBannerTop, priceData),
    [campaignsBannerTop, priceData]
  );
  const resultBannerTop = campaignBoxesBannerTop.length ? campaignBoxesBannerTop : [];

  const globalCampaignsBoxesBannerTop = useMemo(
    () => getCampaignBoxes(globalCampaignsBannerTop),
    [globalCampaignsBannerTop]
  );
  const resultGlobalCampaignsTop = globalCampaignsBoxesBannerTop.length
    ? globalCampaignsBoxesBannerTop
    : [];

  const prodHasPrice = products.some((prod) => prod.productSellingPrice !== null);

  const sections = useMemo(
    () =>
      liferayPageCrossLine.bodyItem &&
      liferayPageCrossLine.bodyItem?.find((item) => item?.contentBody)
        ? liferayPageCrossLine.bodyItem?.map((item) => item)
        : [],
    [liferayPageCrossLine?.bodyItem]
  );
  const bodyItemsAbove = useMemo(
    () => sections.filter((item) => !!item?.contentBody && item?.position?.[0] !== "Underneath"),
    [sections]
  );
  const bodyItemsUnderneath = useMemo(
    () => sections.filter((item) => !!item?.contentBody && item?.position?.[0] === "Underneath"),
    [sections]
  );

  const tabsAbove = useTabs(
    bodyItemsAbove,
    labelManagerLabels?.productList,
    sections?.length > 0 ? "Above" : null
  );
  const tabsUnderneath = useTabs(bodyItemsUnderneath);
  const tabs = tabsAbove.concat(tabsUnderneath);

  return (
    <Layout
      bannerTop={resultBannerTop.length ? resultBannerTop[0] : resultGlobalCampaignsTop[0]}
      location={location}
      newMenu={!!isNewMenu}
    >
      <CompareProvider category={liferayPageCrossLine.slug} products={products}>
        {hasHero && (
          <React.Fragment>
            {
              {
                LiferaySliderHero: (
                  <PreviewSlider isFirstHero data={pclIntro} breadcrumb={breadcrumb} />
                ),
                LiferayEditorial: <EditorialIntro data={pclIntro} breadcrumb={breadcrumb} />,
                LiferayIntro: <Intro data={pclIntro} breadcrumb={breadcrumb} />,
              }[pclIntro.__typename]
            }
          </React.Fragment>
        )}
        {(hasHero || (!hasHero && bodyItemsAbove?.length > 0)) && (
          <PageLineContentBody
            bodyItems={bodyItemsAbove}
            tabs={tabs}
            location={location}
            config={config}
            labelManagerLabels={labelManagerLabels}
            position="Above"
            showTabs={sections?.length > 0}
            breadcrumb={!hasHero && bodyItemsAbove?.length > 0 ? breadcrumb : null}
            pagePath={liferayPageCrossLine.slug}
          />
        )}
        {(!hasHero ||
          (hasHero &&
            !!categoryModule &&
            categoryModule?.__typename === "LiferayCategoryModule")) && (
          <PLPIntro
            title={liferayPageCrossLine?.pclTitle}
            descriptionBefore={liferayPageCrossLine.pclIntroBefore}
            descriptionAfter={liferayPageCrossLine.pclIntroAfter}
            breadcrumb={bodyItemsAbove?.length === 0 ? breadcrumb : null}
            categoryModule={
              !!categoryModule && categoryModule?.__typename === "LiferayCategoryModule"
            }
            hasHero={hasHero}
          />
        )}
        {!!categoryModule && categoryModule?.__typename === "LiferayCategoryModule" && (
          <CategoryModule data={categoryModule} pagePath={liferayPageCrossLine.slug} />
        )}
        {!bodyItemsAbove?.length > 0 && !hasHero && (
          <PageLineContentBody
            bodyItems={bodyItemsAbove}
            tabs={tabs}
            location={location}
            config={config}
            labelManagerLabels={labelManagerLabels}
            position="Above"
            showTabs={sections?.length > 0}
            breadcrumb={!hasHero && bodyItemsAbove?.length > 0 ? breadcrumb : null}
            noHeroTop={true}
            pagePath={liferayPageCrossLine.slug}
          />
        )}
        {sections?.length > 0 && (
          <span id={slugify(labelManagerLabels?.productList)} className="tabs__scroll-anchor" />
        )}
        <ProductListCross
          pageCrossLine={liferayPageCrossLine}
          products={products}
          location={location}
          config={config}
          hasHero={hasHero}
          breadcrumb={breadcrumb}
          bannerPromoCM={resultBannerPromoCm}
          className={sections?.length > 0 && "product-list--with-anchor"}
          categoryModule={
            !!categoryModule && categoryModule?.__typename === "LiferayCategoryModule"
          }
        />
        {liferayProductNotes && prodHasPrice && (
          <ProductNotes
            data={liferayProductNotes}
            position="plp"
            className={bodyItemsUnderneath?.length > 0 && "product-notes--with-modules"}
          />
        )}
        <PageLineContentBody
          bodyItems={bodyItemsUnderneath}
          tabs={tabs}
          location={location}
          config={config}
          labelManagerLabels={labelManagerLabels}
          position="Underneath"
          pagePath={liferayPageCrossLine.slug}
        />
        <CompareBar
          to={`${normalizeRelativePath(liferayPageCrossLine.slug)}compare/`}
          products={products}
          config={config}
        />
      </CompareProvider>
    </Layout>
  );
};

export const Head = ({ data: { liferayPageCrossLine }, location }) => {
  const { descriptionText, seo, pclIntro } = liferayPageCrossLine;
  const ogImage = useMemo(() => {
    if (pclIntro) {
      const module = pclIntro.moduleTitleFieldSet.find((module) =>
        module.titleT1FieldSet?.some(
          (item) => item.bgImageMobileT1FieldSet.bgImageMobileT1 || item.bgImageT1FieldSet.bgImageT1
        )
      );
      const moduleSlider = pclIntro.moduleTitleFieldSet.find((module) =>
        module.titleT1FieldSet?.some(
          (item) => item.slideForegroundImageFieldSet.slideForegroundImage
        )
      );
      return module
        ? module.titleT1FieldSet.find((item) => item.bgImageMobileT1FieldSet.bgImageMobileT1)
          ? module.titleT1FieldSet.find((item) => item.bgImageMobileT1FieldSet.bgImageMobileT1)
              .bgImageMobileT1FieldSet.bgImageMobileT1.node.resize
          : moduleSlider
          ? moduleSlider.titleT1FieldSet.find(
              (item) => item.slideForegroundImageFieldSet.slideForegroundImage
            ).slideForegroundImageFieldSet.slideForegroundImage.node.resize
          : module.titleT1FieldSet.find((item) => item.bgImageT1FieldSet.bgImageT1)
              .bgImageT1FieldSet.bgImageT1.node.resize
        : placeholderLogoImg;
    }
    return placeholderLogoImg;
  }, [pclIntro]);

  const hrefLanguages = useMemo(() => findHrefLang(liferayPageCrossLine), [liferayPageCrossLine]);

  const canonicalPath = useMemo(() => {
    if (typeof window !== "undefined" && location) {
      const searchParams = new URLSearchParams(window.location.search);
      const canonicalParams = new URLSearchParams();
      if (!searchParams.has("page")) {
        return liferayPageCrossLine.slug;
      }
      if (searchParams.has("page")) {
        canonicalParams.set("page", searchParams.get("page"));
      }
      return `${normalizeRelativePath(liferayPageCrossLine.slug)}?${canonicalParams}`;
    }
    return liferayPageCrossLine.slug;
  }, [liferayPageCrossLine, location]);

  return (
    <Seo
      title={liferayPageCrossLine.liferayFields.title}
      description={descriptionText}
      path={canonicalPath}
      ogImage={ogImage}
      seo={seo}
      hrefLanguages={hrefLanguages}
      excludeFromSitemap={liferayPageCrossLine?.excludeFromSitemap}
      noIndexNoFollow={liferayPageCrossLine?.noIndexNoFollow}
    />
  );
};

export default withPreview(PageCrossLineTemplate, {
  subField: "liferayPageCrossLine",
  fixed: true,
});
export const query = graphql`
  query ($id: String) {
    liferayPageCrossLine(id: { eq: $id }) {
      ...ContentBodyCrossLineFragment
      parentPages {
        page {
          ... on LiferayPage {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageLine {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageCrossLine {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageExternalLink {
            liferayFields {
              title
            }
            externalLink
          }
        }
        menuItem {
          name
          useCustomName
        }
      }
      liferayFields {
        siteId
        articleId
        title
        taxonomyCategoryBriefs {
          name
          parentTaxonomyVocabulary {
            name
          }
        }
      }
      excludeFromSitemap
      noIndexNoFollow
      slug
      seo {
        ...SeoFragment
      }
      pclTitle
      pclIntro {
        __typename
        ...SliderHeroFragment
        ...EditorialFragment
        ...IntroFragment
      }
      descriptionText
      pclIntroBefore
      pclIntroAfter
      order
      categoryModule {
        ...CategoryModuleFragment
      }
      pclLines {
        ... on LiferayPageLine {
          liferayFields {
            title
          }
          allBabelProdotto {
            ...BabelProdottoCardFragment
            attributesMap
            pageLine {
              liferayFields {
                title
              }
              lineId
              computedFilters {
                id
                name
                values
                type
                hashMap
              }
            }
          }
          fields {
            feefoRating
          }
        }
      }
      plcSkus
      plcSkusProducts: products {
        ...BabelProdottoCardFragment
        attributesMap
        pageLine {
          liferayFields {
            title
          }
          lineId
          computedFilters {
            id
            name
            values
            type
            hashMap
          }
        }
      }
      filterMagento
      campaignsPLP: campaigns(positions: ["PLP | Banner Promo"]) {
        date {
          pmStart
          pmEnd
        }
        filterField {
          pmInStock
          pmSpecialPrice
        }
        boxAndPosition {
          pmBoxPosition
          webContent {
            ...BannerPromoCMFragment
          }
        }
      }
      campaignsBannerTop: campaigns(positions: ["BANNER TOP | Banner Promo"]) {
        date {
          pmStart
          pmEnd
        }
        filterField {
          pmInStock
          pmSpecialPrice
        }
        boxAndPosition {
          pmBoxPosition
          webContent {
            ...BannerTopMenuFragment
          }
        }
      }
    }
    globalCampaignsBannerTop: globalCampaigns(positions: ["BANNER TOP | Banner Promo"]) {
      date {
        pmStart
        pmEnd
      }
      filterField {
        pmInStock
        pmSpecialPrice
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ...BannerTopMenuFragment
        }
      }
    }
    liferayProductNotes {
      ...ProductNotesFragment
    }
    config: liferayGeneralConfiguration {
      haierStEcommLines {
        productLine
      }
      isReevooActiveFieldSet {
        isReevooActive
        isBazaarVoiceActive
      }
      virtualRealityActiveFieldSet {
        virtualRealityActive
      }
      wtbAndRetailersActive
      tagActive
      tagColor
      invertProductFamily
    }
    labelManagerLabels {
      home
      productList
    }
    newMainMenuFiltered {
      name
    }
    liferayMenuConfiguration {
      newMenuActive
      copyrightText
    }
  }
`;
